import React, { useState } from "react";
import { useContacts } from "../../hooks/useContacts";
import ConfirmationModal from "../../common/ConfirmationModal";
import CustomPagination from "../../common/Pagination";
import moment from "moment";

export default function ContactTablePage() {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const itemsPerPage = 10;

    const { contacts, pagination, isLoading, deleteContact, isDeleting } = useContacts(currentPage, itemsPerPage, searchTerm);

    const [contactToDelete, setContactToDelete] = useState(null);
    const [contactToView, setContactToView] = useState(null);

    const handleSearch = (e) => {
        e?.preventDefault();
        setSearchTerm(searchInput);
        setCurrentPage(1);
    };

    const clearSearch = () => {
        setSearchInput("");
        setSearchTerm("");
        setCurrentPage(1);
    };

    return (
        <div className="container-fluid p-4" style={{ backgroundColor: "#f4f7f6", minHeight: "100vh" }}>

            {/* Top Header to match "User Management" */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0 text-dark" style={{ fontWeight: "500" }}>Contact Management</h4>
                <div className="d-flex gap-2">
                    <button className="btn btn-info text-white fw-medium" style={{ cursor: "default" }}>
                        Total Messages: {pagination?.totalRecords || contacts.length || 0}
                    </button>
                </div>
            </div>

            {/* Search Filters Row */}
            <div className="bg-white p-3 mb-4 rounded shadow-sm d-flex flex-wrap gap-3 align-items-center">
                <div className="flex-grow-1" style={{ maxWidth: "400px" }}>
                    <input
                        type="text"
                        className="form-control mr-2"
                        placeholder="Search..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                </div>
                <button className="btn btn-primary px-4 fw-medium mr-2" onClick={handleSearch}>
                    Search
                </button>
                <button className="btn btn-outline-secondary px-4 fw-medium" onClick={clearSearch}>
                    Reset
                </button>
            </div>

            {/* Main Table Card */}
            <div className="card shadow-sm border-0 rounded">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0 text-nowrap">
                            {/* Matching the pale header from your screenshot */}
                            <thead style={{ backgroundColor: "#f0f2f8" }}>
                                <tr>
                                    <th className="border-0 text-muted py-3 ps-4" style={{ fontWeight: "600" }}>No</th>
                                    <th className="border-0 text-muted py-3" style={{ fontWeight: "600" }}>Date</th>
                                    <th className="border-0 text-muted py-3" style={{ fontWeight: "600" }}>Name</th>
                                    <th className="border-0 text-muted py-3" style={{ fontWeight: "600" }}>Email</th>
                                    <th className="border-0 text-muted py-3" style={{ fontWeight: "600" }}>Subject</th>
                                    <th className="border-0 text-muted py-3 text-center pe-4" style={{ fontWeight: "600" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan="6" className="text-center py-5 text-muted">Loading messages...</td></tr>
                                ) : contacts.length === 0 ? (
                                    <tr><td colSpan="6" className="text-center py-5 text-muted">No contact messages found.</td></tr>
                                ) : (
                                    contacts.map((item, index) => (
                                        <tr key={item._id}>
                                            <td className="ps-4 text-muted">
                                                {/* Calculate accurate row numbers based on pagination */}
                                                {(currentPage - 1) * itemsPerPage + index + 1}
                                            </td>
                                            <td className="text-muted small">
                                                {moment(item.createdAt).format("M/DD/YYYY")}
                                            </td>
                                            <td className="text-muted">{item.username || "-"}</td>
                                            <td className="text-muted">
                                                {item.email || "-"}
                                            </td>
                                            <td>
                                                <span className="text-muted text-truncate d-inline-block" style={{ maxWidth: "250px" }}>
                                                    {item.subject || "-"}
                                                </span>
                                            </td>
                                            <td className="text-center pe-4">
                                                <div className="d-flex justify-content-center gap-2">
                                                    {/* Eye Icon for viewing */}
                                                    <button
                                                        className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center mr-2"
                                                        style={{ width: "32px", height: "32px" }}
                                                        onClick={() => setContactToView(item)}
                                                        title="View Message"
                                                    >View
                                                        {/* <i className="fas fa-eye"></i> */}
                                                    </button>
                                                    {/* Trash Icon exactly like screenshot */}
                                                    <button
                                                        className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center"
                                                        style={{ width: "32px", height: "32px" }}
                                                        onClick={() => setContactToDelete(item)}
                                                        title="Delete"
                                                    >
                                                        <i className="fas fa-trash-alt"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pagination Footer */}
                {pagination && Number(pagination.totalPages) > 1 && (
                    <div className="card-footer bg-white p-3 border-top">
                        <CustomPagination
                            currentPage={currentPage}
                            totalPages={Number(pagination.totalPages)}
                            onPageChange={setCurrentPage}
                            totalItems={Number(pagination.totalRecords)}
                            itemsPerPage={itemsPerPage}
                        />
                    </div>
                )}
            </div>

            {/* VIEW MESSAGE MODAL */}
            {contactToView && (
                <>
                    <div className="modal-backdrop fade show" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}></div>
                    <div className="modal fade show d-block" tabIndex="-1" onClick={() => setContactToView(null)}>
                        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" onClick={e => e.stopPropagation()}>
                            <div className="modal-content shadow border-0 rounded">
                                <div className="modal-header bg-light border-bottom">
                                    <h5 className="modal-title text-dark" style={{ fontWeight: "500" }}>Message Details</h5>
                                    <button type="button" className="btn-close shadow-none" onClick={() => setContactToView(null)}></button>
                                </div>
                                <div className="modal-body p-4">
                                    <div className="mb-4">
                                        <small className="text-muted text-uppercase fw-bold" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>From</small>
                                        <div className="text-dark fs-6 mt-1">{contactToView.username || "Unknown"}</div>
                                        <a href={`mailto:${contactToView.email}`} className="text-decoration-none text-primary">{contactToView.email}</a>
                                    </div>
                                    <div className="mb-4">
                                        <small className="text-muted text-uppercase fw-bold" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Subject</small>
                                        <div className="text-dark fs-6 mt-1">{contactToView.subject || "No Subject"}</div>
                                    </div>
                                    <div className="bg-light p-3 rounded border">
                                        <small className="text-muted text-uppercase fw-bold mb-2 d-block" style={{ fontSize: "11px", letterSpacing: "0.5px" }}>Message</small>
                                        <p className="mb-0 text-dark" style={{ whiteSpace: "pre-wrap", lineHeight: "1.6" }}>
                                            {contactToView.message}
                                        </p>
                                    </div>
                                    <div className="text-end mt-3">
                                        <small className="text-muted">Received on {moment(contactToView.createdAt).format("MMMM Do YYYY, h:mm a")}</small>
                                    </div>
                                </div>
                                <div className="modal-footer border-top bg-light">
                                    <button type="button" className="btn btn-outline-secondary px-4" onClick={() => setContactToView(null)}>Close</button>
                                    <a href={`mailto:${contactToView.email}?subject=RE: ${encodeURIComponent(contactToView.subject)}`} className="btn btn-primary px-4">
                                        Reply via Email
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* DELETE MODAL */}
            <ConfirmationModal
                show={contactToDelete !== null}
                onClose={() => setContactToDelete(null)}
                onConfirm={() => {
                    deleteContact(contactToDelete._id);
                    setContactToDelete(null);
                }}
                isLoading={isDeleting}
                title="Delete Message"
                confirmButtonVariant="danger"
            >
                Are you sure you want to delete the message from <strong>{contactToDelete?.username}</strong>? This action cannot be undone.
            </ConfirmationModal>
        </div>
    );
}